import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { PermissionsProvider } from './context/PermissionsContext';
import PrivateRoute from './utils/PrivateRoute';

// Components
import Navbar from './components/Layout/Navbar';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';

// Pages
import DashboardPage from './pages/DashboardPage';
import OffersPage from './pages/OffersPage';
import NewOfferPage from './pages/NewOfferPage';
import RequestsAndMatchingPage from './pages/RequestsAndMatchingPage';
import NewRequestPage from './pages/NewRequestPage';
import CatalogPage from './pages/CatalogPage';
import StatisticsPage from './pages/StatisticsPage';
import TutorialsPage from './pages/TutorialsPage';
import FAQPage from './pages/FAQPage';
import ChatPage from './pages/ChatPage';
import SettingsPage from './pages/SettingsPage';
import FollowingPage from './pages/FollowingPage';
import PublicRequestForm from './pages/PublicRequestForm';
import PublicOfferForm from './pages/PublicOfferForm';
import PublicCatalogView from './pages/PublicCatalogView';
import PublicOfferView from './pages/PublicOfferView';
import InvoicesPage from './pages/InvoicesPage';
import NotaryOfficesPage from './pages/NotaryOfficesPage';
import NotificationsPage from './pages/NotificationsPage';
import MarketerProfilePage from './pages/MarketerProfilePage';
import LandingPage from './pages/LandingPage';
import TeamPage from './pages/TeamPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import SubscriptionPage from './pages/SubscriptionPage';
import PublicMarketerLanding from './pages/PublicMarketerLanding';
import PublicMarketerPreview from './pages/PublicMarketerPreview';

import { OnboardingProvider, useOnboarding } from './context/OnboardingContext';
import { NetworkStatusProvider } from './context/NetworkStatusContext';
import OnboardingOverlay from './components/Onboarding/OnboardingOverlay';
import HelpButton from './components/HelpButton';
import './App.css';

const OnboardingTrigger = () => {
  const { startOnboarding, isCompleted } = useOnboarding();
  const { user } = useAuth();

  React.useEffect(() => {
    if (user && !isCompleted()) {
      const timer = setTimeout(() => startOnboarding(), 800);
      return () => clearTimeout(timer);
    }
  }, [user, isCompleted, startOnboarding]);

  return null;
};

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const { i18n } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const isIos = React.useMemo(() => /iphone|ipad|ipod/i.test(window.navigator.userAgent), []);
  const isInStandaloneMode = React.useMemo(() => {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  }, []);
  const [installPromptDismissed, setInstallPromptDismissed] = React.useState(() => {
    return localStorage.getItem('msk_install_prompt_dismissed') === '1';
  });

  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  React.useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setInstallPromptDismissed(true);
      localStorage.setItem('msk_install_prompt_dismissed', '1');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallApp = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setDeferredPrompt(null);
      setInstallPromptDismissed(true);
      localStorage.setItem('msk_install_prompt_dismissed', '1');
    }
  };

  const dismissInstallPrompt = () => {
    setInstallPromptDismissed(true);
    localStorage.setItem('msk_install_prompt_dismissed', '1');
  };

  const showWebInstallPrompt = Boolean(deferredPrompt) && !installPromptDismissed;
  const showIosInstallHint = isIos && !isInStandaloneMode && !installPromptDismissed && !deferredPrompt;

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/request-form" element={<PublicRequestForm />} />
        <Route path="/offer-form" element={<PublicOfferForm />} />
        <Route path="/offer/:offerId" element={<PublicOfferView />} />
        <Route path="/c/:slug" element={<PublicCatalogView />} />
        {/* Keeps marketer catalog pages matched before the general marketer profile route. */}
        <Route path="/p/:username/catalog/:slug" element={<PublicCatalogView />} />
        <Route path="/p/:username" element={<PublicMarketerLanding />} />
        <Route path="/preview/marketer" element={<PublicMarketerPreview />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public pages — no navbar, even when logged in */}
      {/* Keeps marketer catalog pages matched before the general marketer profile route. */}
      <Route path="/p/:username/catalog/:slug" element={<PublicCatalogView />} />
      <Route path="/p/:username" element={<PublicMarketerLanding />} />
      <Route path="/preview/marketer" element={<PublicMarketerPreview />} />
      <Route path="/c/:slug" element={<PublicCatalogView />} />
      <Route path="/offer/:offerId" element={<PublicOfferView />} />
      <Route path="*" element={
        <div className="app-layout">
          <Navbar />
          <OnboardingTrigger />
          <OnboardingOverlay />
          <HelpButton />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
              <Route path="/offers" element={<PrivateRoute><OffersPage /></PrivateRoute>} />
              {/* All internal offer creation now uses one protected standalone page. */}
              <Route path="/offers/new" element={<PrivateRoute><NewOfferPage /></PrivateRoute>} />
              <Route path="/requests" element={<PrivateRoute><RequestsAndMatchingPage /></PrivateRoute>} />
              {/* All internal request creation now uses one protected standalone page. */}
              <Route path="/requests/new" element={<PrivateRoute><NewRequestPage /></PrivateRoute>} />
              {/* السوق متوقف مؤقتاً؛ أبقينا الصفحة ومنطقها لكن منعنا فتحها مباشرة. */}
              <Route path="/network" element={<Navigate to="/dashboard" replace />} />
              <Route path="/catalog" element={<PrivateRoute><CatalogPage /></PrivateRoute>} />
              <Route path="/matching" element={<Navigate to="/requests" replace />} />
              <Route path="/statistics" element={<PrivateRoute><StatisticsPage /></PrivateRoute>} />
              <Route path="/tutorials" element={<PrivateRoute><TutorialsPage /></PrivateRoute>} />
              <Route path="/faq" element={<PrivateRoute><FAQPage /></PrivateRoute>} />
              <Route path="/chat" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
              <Route path="/following" element={<PrivateRoute><FollowingPage /></PrivateRoute>} />
              <Route path="/invoices" element={<PrivateRoute><InvoicesPage /></PrivateRoute>} />
              <Route path="/notary-offices" element={<PrivateRoute><NotaryOfficesPage /></PrivateRoute>} />
              <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
              <Route path="/network/marketer/:marketerId" element={<PrivateRoute><MarketerProfilePage /></PrivateRoute>} />
              <Route path="/team" element={<PrivateRoute><TeamPage /></PrivateRoute>} />
              <Route path="/subscription" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
              <Route path="/request-form" element={<PublicRequestForm />} />
              <Route path="/offer-form" element={<PublicOfferForm />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>
          {showWebInstallPrompt && (
            <div className="install-prompt-banner" role="status" aria-live="polite">
              <div className="install-prompt-content">
                <strong>إضافة للتطبيقات</strong>
                <span>يمكنك إضافة منصة مسك إلى الشاشة الرئيسية لفتحها كتطبيق.</span>
              </div>
              <div className="install-prompt-actions">
                <button className="btn btn-outline btn-sm" onClick={dismissInstallPrompt}>لاحقاً</button>
                <button className="btn btn-primary btn-sm" onClick={handleInstallApp}>تثبيت التطبيق</button>
              </div>
            </div>
          )}
          {showIosInstallHint && (
            <div className="install-prompt-banner ios-install-banner" role="status" aria-live="polite">
              <div className="install-prompt-content">
                <strong>تثبيت التطبيق على iPhone</strong>
                <span>من Safari اضغط مشاركة ثم اختر "Add to Home Screen" لإضافة التطبيق بملء الشاشة.</span>
              </div>
              <div className="install-prompt-actions">
                <button className="btn btn-outline btn-sm" onClick={dismissInstallPrompt}>فهمت</button>
              </div>
            </div>
          )}
        </div>
      } />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <NetworkStatusProvider>
            <PermissionsProvider>
              <ToastProvider>
                <OnboardingProvider>
                  <AppContent />
                </OnboardingProvider>
              </ToastProvider>
            </PermissionsProvider>
          </NetworkStatusProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
